import { orderConfirmationFormIds } from "common/component/OrderInformationForm/FormStructure";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import {
    CustomFormFieldTypes,
    NonStandardIds,
    useCreateOrderProps,
    ValidationRule,
    NonStandardValidationRule,
    StandardValidationRule,
    ErrorProps,
    detailsStructure,
    buildingFormat,
    onSubmitOrderProps,
    valueType,
} from "interfaces";
import { formatNonStandardOrderForITO, generateNonStandardErrorId } from "./useNonstandardOrder";
import { useEffect, useMemo, useState } from "react";
import { SubmitOrderCommandInput, SubmitOrderCommandOutput } from "@amzn/ito-client";
import { CSRFService } from "services/csrf-service";
import { ITOOrdersService } from "services/orders/ito-orders-service";
import { GetCatalogErrorContent } from "common/constants";
import { formatStandardOrderForITO } from "./useStandardOrder";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import { ITSE_LABEL } from "helpers";
import { TextContent } from "@amzn/awsui-components-react";
import Paths from "common/paths/paths";
import { useNavigate } from "react-router-dom";
import { UserPermissions } from "services/user-permissions";

const getErrorStringPath = "common.component.LocalItOrderConfirmationContent.orderForm.errors";

const orderCreationStringPath = "errors.ito.orderCreation";

/**
 * 5MB in bytes
 */
const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * The maximum size allowed for an input quantity
 */
const MAX_QUANTITY_SIZE = 2 ** 30;

/**
 * Alphanumeric pattern that only allows letters and numbers
 */
const ALPHANUMERIC_PATTERN = /^[a-zA-Z0-9]*$/;

/**
 * Form field validation rules and error messages for the order information form
 */
const VALIDATION_RULES: Record<string, ValidationRule> = {
    [orderConfirmationFormIds.behalfUser]: {
        validate: (value) => ALPHANUMERIC_PATTERN.test(value.trim()),
        errorMessage: getString(`${getErrorStringPath}.behalfUser`),
    },
    [orderConfirmationFormIds.costCenter]: {
        validate: (value) => value.trim().length > 0 && ALPHANUMERIC_PATTERN.test(value.trim()),
        errorMessage: getString(`${getErrorStringPath}.costCenter`),
    },
    [orderConfirmationFormIds.companyGLCode]: {
        validate: (value) =>
            ALPHANUMERIC_PATTERN.test(value.trim()) &&
            (value.trim().length == 0 || (value.trim().length >= 2 && value.trim().length <= 4)),
        errorMessage: getString(`${getErrorStringPath}.companyGLCode`),
    },
    [orderConfirmationFormIds.locationGLCode]: {
        validate: (value) =>
            ALPHANUMERIC_PATTERN.test(value.trim()) &&
            (value.trim().length == 0 || (value.trim().length >= 4 && value.trim().length <= 5)),
        errorMessage: getString(`${getErrorStringPath}.locationGLCode`),
    },
    [orderConfirmationFormIds.financeCode]: {
        validate: (value) => value.length > 0,
        errorMessage: getString(`${getErrorStringPath}.financeCode`),
    },
    [orderConfirmationFormIds.deliverTo]: {
        validate: (value) => value.length > 0,
        errorMessage: getString(`${getErrorStringPath}.deliverTo`),
    },
    [orderConfirmationFormIds.justification]: {
        validate: (value) => value.length > 0,
        errorMessage: getString(`${getErrorStringPath}.justification`),
    },
    [orderConfirmationFormIds.expediteDate]: {
        validate: (value, expedite) => {
            if (!expedite) return true;
            if (value.length === 0) return false;

            const date = new Date(value);
            const today = new Date();

            return !isNaN(date.getTime()) && date > today;
        },
        errorMessage: getString(`${getErrorStringPath}.expediteDate`),
    },
    [orderConfirmationFormIds.financeForm]: {
        validate: (value) => {
            if (!value) return true;
            const files = value as File[];
            if (files.length === 0) return true;

            for (const file of files) {
                if (file.size > MAX_FILE_SIZE) {
                    return false;
                }
            }

            return true;
        },
        errorMessage: getString(`${getErrorStringPath}.financeForm`),
        type: "default",
    },
};

/**
 * Form field validation rules, error tags, and error messages for the non standard item form
 */
const NON_STANDARD_VALIDATION_RULES: Record<keyof typeof NonStandardIds, NonStandardValidationRule> = {
    sku: {
        validate: (value: string) => value.length > 0,
        errorTag: (id: string) => generateNonStandardErrorId(id, "sku"),
        errorMessage: () => getString(`${getErrorStringPath}.sku`),
    },
    manufacturer: {
        validate: (value: string) => value.length > 0,
        errorTag: (id: string) => generateNonStandardErrorId(id, "manufacturer"),
        errorMessage: () => getString(`${getErrorStringPath}.manufacturer`),
    },
    quantity: {
        validate: (value: string) => Number(value) > 0 && Number(value) < MAX_QUANTITY_SIZE,
        errorTag: (id: string) => generateNonStandardErrorId(id, "quantity"),
        errorMessage: () => getString(`${getErrorStringPath}.quantity`),
    },
};

/**
 * Form field validation rules and error messages for the standard item form
 */
const STANDARD_VALIDATION_RULES: Record<string, StandardValidationRule> = {
    taxonomyId: {
        validate: (value) => value > 0 && value < MAX_QUANTITY_SIZE,
        errorMessage: getString(`${getErrorStringPath}.quantity`),
    },
};

/**
 * The default cost center used in the form
 */
const DEFAULT_COST_CENTER = 7498;

/**
 * Custom hook for that returns the setter and getter for the useCreateOrder hook
 *
 * @returns {object} - The setter and getter for the useCreateOrder hook
 */
export const getUseCreateOrder = () => {
    const DEFAULT_VALUES: [string, CustomFormFieldTypes][] = [
        [orderConfirmationFormIds.expedite, false],
        [orderConfirmationFormIds.costCenter, DEFAULT_COST_CENTER.toString()],
    ];

    const [formValues, setFormValues] = useState<Map<string, CustomFormFieldTypes>>(new Map(DEFAULT_VALUES));

    const [formErrors, setFormErrors] = useState<Map<string, string>>(new Map());

    return { formValues, setFormValues, formErrors, setFormErrors };
};

/**
 * Custom hook for managing order creation form validation and submission
 *
 * @param {Map<string, CustomFormFieldTypes>} formValues - Map containing form field values
 * @returns {object} - An object containing error checking function and form submit handler
 */
const useCreateOrder = ({
    formValues,
    isOrderStandard,
    standardOrder,
    nonStandardOrder,
    formErrors,
    setFormErrors,
    onSubmitOrder,
    categories,
    generateError,
}: useCreateOrderProps) => {
    /**
     * Get the string value of a field from the form values
     *
     * @param {string} fieldId - The ID of the form field
     * @returns {string} - The string value of the field or empty string
     */
    const getValue = (fieldId: string, type?: valueType): string | any => {
        if (type === "default") {
            return formValues.get(fieldId);
        }

        return formValues.get(fieldId)?.toString() || "";
    };

    /**
     * Gets the expedite flag for date validation
     *
     * @param {string} fieldId - The ID of the form field
     * @returns {boolean | undefined} - The expedite flag or undefined
     */
    const getExpedite = (fieldId: string): boolean | undefined => {
        return fieldId === orderConfirmationFormIds.expediteDate
            ? (formValues.get(orderConfirmationFormIds.expedite) as boolean)
            : undefined;
    };

    /**
     * Remove errors for fields that are now valid
     *
     * @returns {void} - No return value
     */
    const removeErrors = (): void => {
        const newErrors = new Map<string, string>(formErrors);

        Object.entries(VALIDATION_RULES).forEach(([fieldId, rule]) => {
            const value = getValue(fieldId, rule.type);
            const expedite = getExpedite(fieldId);

            if (rule.validate(value, expedite)) {
                newErrors.delete(fieldId);
            }
        });

        if (!isOrderStandard) {
            nonStandardOrder.forEach((order) => {
                Object.entries(NON_STANDARD_VALIDATION_RULES).forEach(([fieldId, rule]) => {
                    const value = order[fieldId as keyof typeof order]?.toString() || "";

                    if (rule.validate(value)) {
                        newErrors.delete(rule.errorTag(order.id));
                    }
                });
            });
        } else {
            standardOrder.forEach((value, key) => {
                const rule = STANDARD_VALIDATION_RULES.taxonomyId;
                if (rule.validate(value)) {
                    newErrors.delete(key.taxonomyId);
                }
            });
        }

        setFormErrors(newErrors);
    };

    /**
     * Validate all non standard form fields and set errors
     *
     * @param {Map<string, string>} newErrors - Map to store validation errors
     * @returns {boolean} - If the parameters are valid
     */
    const validateNonStandardParameters = (newErrors: Map<string, string>): boolean => {
        let areParametersValid = true;

        nonStandardOrder.forEach((order) => {
            Object.entries(NON_STANDARD_VALIDATION_RULES).forEach(([fieldId, rule]) => {
                const value = order[fieldId as keyof typeof order]?.toString() || "";

                if (!rule.validate(value)) {
                    newErrors.set(rule.errorTag(order.id), rule.errorMessage());
                    areParametersValid = false;
                }
            });
        });

        return areParametersValid;
    };

    /**
     * Validate all standard form fields (The quantity)
     *
     * @param {Map<string, string>} newErrors - Map to store validation errors
     * @returns {boolean} - If the parameters are valid
     */
    const validateStandardParameters = (newErrors: Map<string, string>): boolean => {
        let areParametersValid = true;

        standardOrder.forEach((value, key) => {
            const rule = STANDARD_VALIDATION_RULES.taxonomyId;
            if (!rule.validate(value)) {
                newErrors.set(key.taxonomyId, rule.errorMessage);
                areParametersValid = false;
            }
        });

        return areParametersValid;
    };

    /**
     * Validate all form fields and set errors
     *
     * @returns {boolean} - If the parameters are valid
     */
    const validateParameters = (): boolean => {
        const newErrors = new Map<string, string>();

        let areParametersValid = true;

        Object.entries(VALIDATION_RULES).forEach(([fieldId, rule]) => {
            const value = getValue(fieldId, rule.type);
            const expedite = getExpedite(fieldId);

            if (!rule.validate(value, expedite)) {
                newErrors.set(fieldId, rule.errorMessage);
                areParametersValid = false;
            }
        });

        if (!isOrderStandard) {
            const nonStandardValid = validateNonStandardParameters(newErrors);
            areParametersValid = areParametersValid && nonStandardValid;
        } else {
            const standardValid = validateStandardParameters(newErrors);
            areParametersValid = areParametersValid && standardValid;
        }

        setFormErrors(newErrors);
        return areParametersValid;
    };

    useEffect(() => {
        removeErrors();
    }, [formValues, nonStandardOrder, standardOrder]);

    /**
     * Get error message for a specific field
     *
     * @param {string} id - The ID of the form field
     * @returns {string | undefined} - The error message or undefined if no error
     */
    const getHasError = (id: string): string | undefined => formErrors.get(id);

    /**
     * Get non-standard order error message
     *
     * @returns {string | undefined} - The non-standard order error message or undefined if no error
     */
    const getNonStandardOrderError = (): string | undefined => formErrors.get("nonStandardOrderError");

    /**
     * Format order items for order creation based on (standard or non-standard)
     *
     * @returns {Promise<SubmitOrderCommandInput>} - Formatted order input with items
     */
    const formatOrderItems = async (): Promise<SubmitOrderCommandInput> => {
        let request: SubmitOrderCommandInput = {
            items: undefined,
        };

        if (isOrderStandard) {
            if (standardOrder.size === 0) throw new Error(getString(`${orderCreationStringPath}.emptyStandardItems`));

            request.items = formatStandardOrderForITO(standardOrder);
        } else {
            if (nonStandardOrder.length === 0)
                throw new Error(getString(`${orderCreationStringPath}.emptyNonStandardItems`));

            request.items = await formatNonStandardOrderForITO(nonStandardOrder);
        }

        return request;
    };

    /**
     * Format order items and requester for the order creation
     *
     * @returns {Promise<SubmitOrderCommandInput>} - Complete formatted order input
     */
    const formatOrderInput = async (): Promise<SubmitOrderCommandInput> => {
        let request = await formatOrderItems();

        const onBehalf = formValues.get(orderConfirmationFormIds.behalfUser) as string | undefined;

        if (onBehalf) {
            request.requesterOverride = onBehalf;
        }

        return request;
    };

    /**
     * Variable that represents the available parent categories
     */
    const availableCategories = useMemo(() => {
        return categories?.filter((category) => category.parentTaxonomyId === "") ?? [];
    }, [categories]);

    /**
     * Function that retrieves the requested categories based on the selected items
     *
     * @returns {string[]} selected categories
     */
    const getRequestedCategories = (): string[] => {
        if (!isOrderStandard) {
            return ["Non Standard"];
        }

        const requestedCategories: Set<string> = new Set();
        standardOrder.forEach((_, key) => {
            key.categories?.forEach((categoryId) => {
                const category = availableCategories.find((cat) => cat.taxonomyId === categoryId);
                if (category) {
                    requestedCategories.add(category.name);
                }
            });
        });

        return Array.from(requestedCategories);
    };

    /**
     * Format order details from form values
     *
     * @returns {detailsStructure} - Formatted order details structure
     */
    const formatDetailsForOrder = (): detailsStructure => {
        const financialCodeOptions = formValues.get(orderConfirmationFormIds.financeCode) as OptionDefinition;
        const financialCode = financialCodeOptions.value;
        if (!financialCode) throw new Error(getString(`${orderCreationStringPath}.emptyFinancialCode`));

        const expediteDateString = formValues.get(orderConfirmationFormIds.expediteDate) as string | undefined;

        return {
            requestType: isOrderStandard ? "Standard" : "NonStandard",
            isExpedite: formValues.get(orderConfirmationFormIds.expedite) as boolean,
            costCenter: (formValues.get(orderConfirmationFormIds.costCenter) as string)?.trim(),
            projectCode: (formValues.get(orderConfirmationFormIds.project) as string)?.trim(),
            financialCode,
            glCode: (formValues.get(orderConfirmationFormIds.companyGLCode) as string)?.trim(),
            businessJustification: (formValues.get(orderConfirmationFormIds.justification) as string)?.trim(),
            locationGlCode: (formValues.get(orderConfirmationFormIds.locationGLCode) as string)?.trim(),
            expediteDate: expediteDateString ? new Date(expediteDateString) : undefined,
            requestCategories: getRequestedCategories(),
        };
    };

    /**
     * Format delivery location from form values
     *
     * @returns {buildingFormat} - Formatted building location data
     */
    const formatDeliveryLocation = (): buildingFormat => {
        const deliveryValues = formValues.get(orderConfirmationFormIds.deliverTo) as OptionDefinition | undefined;
        if (!deliveryValues) throw new Error(getString(`${orderCreationStringPath}.emptyDeliveryTo`));

        const buildings = deliveryValues.value;

        if (!buildings) throw new Error(getString(`${orderCreationStringPath}.deliverToFormatError`));

        const buildingData = JSON.parse(buildings);

        if (!buildingData) throw new Error(getString(`${orderCreationStringPath}.failedToFindBuilding`));

        return buildingData;
    };

    /**
     * Format order details from form values
     *
     * @returns {string[]} - Formatted order details structure
     */
    const getWatchersForOrder = (): { watchers: string[] } => {
        const watchers = formValues.get(orderConfirmationFormIds.watchers) as string[];

        return {
            watchers
        };
    };

    /**
     * Checks if the customOnSubmit function is loading
     */
    const [isLoading, setIsLoading] = useState<boolean>(false);

    /**
     * Handle form submission with validation
     *
     *
     * @param {React.FormEvent<HTMLFormElement>} e - The form submission event
     * @returns {void} - No return value
     */
    const customOnSubmitForm: React.FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault();

        setIsLoading(true);

        /** Parameters Validation */
        const parametersAreValid = validateParameters();

        if (!parametersAreValid) {
            setIsLoading(false);
            return;
        }

        /** Parameters Retrieval / Formatting */
        let request;
        try {
            request = await formatOrderInput();
        } catch (error) {
            generateError({
                name: getString(`${getErrorStringPath}.nonStandardError`),
                content: GetCatalogErrorContent("inverted"),
            });
            setIsLoading(false);
            return;
        }

        const details = formatDetailsForOrder();

        const deliveryLocation = formatDeliveryLocation();

        const { watchers } = getWatchersForOrder();

        request.labels = [ITSE_LABEL];

        const attachments = formValues.get(orderConfirmationFormIds.financeForm) as File[] | undefined;

        await onSubmitOrder({
            request,
            details,
            watchers,
            deliveryLocation,
            attachments,
        });
        setIsLoading(false);
    };

    return {
        getHasError,
        customOnSubmitForm,
        isLoading,
    };
};

interface ITOError extends Error {
    $metadata?: {
        httpStatusCode?: number;
    };
}

/**
 * Navigation state for a created order
 *
 * @interface
 * @property {object} order - The order information object
 * @property {boolean} order.created - Flag indicating if the order was successfully created
 * @property {string} [order.reference] - The unique reference identifier of the created order
 * @property {string} [order.taxonomyId] - The taxonomy identifier associated with the order
 * @property {boolean} order.fileUploadFailed - Flag indicating if file upload failed during order creation
 */
export interface NavigateOnCreatedOrder {
    order: {
        created: boolean;
        reference?: string;
        taxonomyId?: string;
        fileUploadFailed: boolean;
        failedFiles: string[];
    };
}

/**
 * Custom hook for submitting orders to the ITO service
 *
 * @returns {ErrorProps | undefined} error - Error state if submission fails
 * @returns {boolean} isLoading - Loading state during order submission
 * @returns {function} onSubmitOrder - Function to submit the order
 * @returns {SubmitOrderCommandOutput | undefined} order - The submitted order result
 */
export const useSubmitOrder = () => {
    const [error, setError] = useState<ErrorProps>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [order, setOrder] = useState<SubmitOrderCommandOutput>();

    const csrfService = new CSRFService();
    const itoService = new ITOOrdersService();

    interface uploadFileOutput {
        failedFiles: string[];
        failedUpload: boolean;
    }

    /**
     * Upload finance form file to the order
     *
     * @param {string} csrfToken - CSRF token for secure file upload
     * @param {File} [attachments] - Optional finance form file to upload
     * @param {string} [orderId] - Optional order ID to associate with the file
     * @returns {Promise<void>} - Promise that resolves when file upload is complete
     */
    const uploadFiles = async (
        csrfToken: string,
        attachments?: File[],
        orderId?: string,
    ): Promise<uploadFileOutput> => {
        if (!attachments) {
            setIsLoading(false);
            return { failedFiles: [], failedUpload: false };
        }

        let failedFiles: string[] = [];
        let failedUpload = false;

        if (!orderId) throw new Error(getString(`${orderCreationStringPath}.missingOrderId`));

        for (const attachment of attachments) {
            if (attachment.size > MAX_FILE_SIZE) {
                failedUpload = true;
                failedFiles.push(attachment.name);
                continue;
            }

            try {
                await itoService.uploadOrderFile(orderId, attachment, attachment.type, attachment.name, csrfToken);
            } catch (error) {
                failedUpload = true;
                failedFiles.push(attachment.name);
            }
        }

        setIsLoading(false);
        return { failedFiles, failedUpload };
    };

    const navigate = useNavigate();

    /**
     * Navigate to order confirmation page after successful order creation
     *
     * @param {SubmitOrderCommandOutput} order - The successfully created order
     * @returns {void} - No return value
     */
    const uploadedOrder = (order: SubmitOrderCommandOutput, fileUploadFailed: boolean, failedFiles: string[]) => {
        if (!order) return;

        const windowNavigation = Paths.itOrderList.url;

        const parameters: NavigateOnCreatedOrder = {
            order: {
                created: true,
                reference: order.orderReference,
                taxonomyId: order.orderId,
                fileUploadFailed,
                failedFiles,
            },
        };

        navigate(windowNavigation, {
            state: {
                ...parameters,
            },
        });
    };

    /**
     * Submit order to ITO service with CSRF protection
     *
     * @param {onSubmitOrderProps} props - Object containing request, details, and delivery location
     * @param {SubmitOrderCommandInput} props.request - The order request data
     * @param {detailsStructure} props.details - Order details including financial information
     * @param {buildingFormat} props.deliveryLocation - Delivery location information
     *
     * @returns {Promise<void>} - Promise that resolves when order submission is complete
     */
    const onSubmitOrder = async ({
        request,
        details,
        deliveryLocation,
        watchers,
        attachments,
    }: onSubmitOrderProps): Promise<void> => {
        setIsLoading(true);

        let csrfToken;

        try {
            csrfToken = await csrfService.getToken();
        } catch (error) {
            setError({
                name: getString(`${orderCreationStringPath}.csrfTokenFailed`),
                content: GetCatalogErrorContent("inverted"),
            });
            setIsLoading(false);
            return;
        }

        if (request.requesterOverride !== undefined && request.requesterOverride.length > 0) {
            if (!(await UserPermissions.instance.isValidAmazonianLogin(request.requesterOverride))) {
                setError({
                    name: getString(`${orderCreationStringPath}.invalidOnBehalfUser.name`),
                    content: getString(`${orderCreationStringPath}.invalidOnBehalfUser.content`),
                });
                setIsLoading(false);
                return;
            }
        }

        let order;

        try {
            order = await itoService.CreateOrder(request, csrfToken, details, deliveryLocation);
            setOrder(order);
        } catch (error) {
            const itoError = error as ITOError;
            const is404 = itoError.$metadata?.httpStatusCode === 404;
            const itemNotFoundError = getString("itemNotFound", orderCreationStringPath, {
                username: request.requesterOverride || UserPermissions.instance.getUsername,
            });
            const errorName = is404 ? itemNotFoundError : getString(`${orderCreationStringPath}.orderCreationFailed`);

            setError({
                name: errorName,
                content: GetCatalogErrorContent("inverted"),
            });
            setIsLoading(false);
            return;
        }

        try {
            await Promise.all(
                watchers.map(watcher => 
                    itoService.addOrderWatchers(order.orderId!, watcher, csrfToken)
                )
            );
        } catch (error) {
            setError({
                name: (error as Error).name,
                content: <TextContent>{getString(`${orderCreationStringPath}.addOrderWatchers`)}</TextContent>,
            });
        }

        let files: uploadFileOutput = { failedFiles: [], failedUpload: false };

        try {
            files = await uploadFiles(csrfToken, attachments, order?.orderId);
        } catch (error) {
            setError({
                name: (error as Error).name,
                content: <TextContent>{getString(`${orderCreationStringPath}.failedToUploadFile`)}</TextContent>,
            });
        }

        setIsLoading(false);
        if (order) {
            uploadedOrder(order, files.failedUpload, files.failedFiles);
        }
    };

    return { error, isLoading, onSubmitOrder, order };
};

export default useCreateOrder;
